import React, { useState, useEffect } from "react";
import { Input, Button, Form, Alert } from "antd";
import { LockOutlined, UserOutlined } from "@ant-design/icons";
import { Link, useHistory } from "react-router-dom";
import flag from "../SignIn/TanzaniaFlag1.jpeg";
import emblem from "../SignIn/TanzaniaEmblem1.jpeg";
import "../SignIn/styles.css";
import API from "../../../../API";
import { notifySuccess, notifyError } from "../../../../Util";

/**
 * @component
 * @name ResetPasswordForm
 * @description Complete password reset form with token validation
 */
const ResetPasswordForm = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [passwordValidation, setPasswordValidation] = useState({ isValid: false, requirements: [] });
  const [form] = Form.useForm();
  const history = useHistory();

  const getUrlParams = () => {
    const hash = window.location.hash;
    if (hash.includes('?')) {
      const queryString = hash.split('?')[1];
      const urlParams = new URLSearchParams(queryString);
      return {
        token: urlParams.get('token'),
        email: urlParams.get('email')
      };
    }
    const urlParams = new URLSearchParams(window.location.search);
    return {
      token: urlParams.get('token'),
      email: urlParams.get('email')
    };
  };

  const { token, email } = getUrlParams();

  useEffect(() => {
    if (!token || !email) {
      setError("Invalid reset link. Please request a new password reset.");
    }
  }, [token, email]);

  const handlePasswordChange = (password) => {
    if (password) {
      const validation = API.validatePassword(password);
      setPasswordValidation(validation);
    } else {
      setPasswordValidation({ isValid: false, requirements: [] });
    }
  };

  /**
   * @function
   * @name onFinish
   * @description handles form submission for password reset
   * @param {Object} values
   */
  const onFinish = async (values) => {
    if (!token || !email) {
      setError("Invalid reset link. Please request a new password reset.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      await API.resetPassword({
        email: email,
        token: token,
        password: values.password,
        password_confirmation: values.confirmPassword
      });

      notifySuccess("Password reset successfully! You can now log in with your new password.");
      
      setTimeout(() => {
  history.push('/signin');
      }, 2000);

    } catch (err) {
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.errors?.password?.[0] ||
                          err.response?.data?.errors?.token?.[0] ||
                          "Failed to reset password. Please try again or request a new reset link.";
      setError(errorMessage);
      notifyError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!token || !email) {
    return (
      <div className="SignIn">
        <div className="SignInForm">
          <img src={flag} alt="Tanzanian flag" className="national-flag"></img>
          <div className="content-body">
            <img
              src={emblem}
              alt="Tanzanian national emblem"
              className="national-embelem"
            ></img>
            <div>
              <div className="Logo">
                <h5>Invalid Reset Link</h5>
              </div>
              <Alert
                message="Invalid or expired reset link"
                description="Please request a new password reset from the login page."
                type="error"
                showIcon
                style={{ marginBottom: '16px' }}
              />
              <div style={{ display: "flex", justifyContent: "center" }}>
                <Link to="/signin">Back to Log in</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="SignIn">
      <div className="SignInForm">
        <img src={flag} alt="Tanzanian flag" className="national-flag"></img>
        <div className="content-body">
          <img
            src={emblem}
            alt="Tanzanian national emblem"
            className="national-embelem"
          ></img>
          <div>
            <div className="Logo">
              <h5>Reset Your Password</h5>
            </div>
            <div>
              <p>Enter your new password below</p>
            </div>
            
            {error && (
              <Alert
                message={error}
                type="error"
                showIcon
                style={{ marginBottom: '16px' }}
                closable
                onClose={() => setError('')}
              />
            )}

            <Form 
              form={form}
              autoComplete="off" 
              onFinish={onFinish}
              layout="vertical"
              data-testid="reset-password-form"
            >
              {/* Email field (read-only) */}
              <Form.Item label="Email">
                <Input
                  prefix={<UserOutlined style={{ color: "rgba(0,0,0,.25)" }} />}
                  value={email}
                  disabled
                />
              </Form.Item>

              {/* New Password field */}
              <Form.Item
                name="password"
                label="New Password"
                rules={[
                  {
                    required: true,
                    message: "Please input your new password!",
                  },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const validation = API.validatePassword(value);
                      return validation.isValid 
                        ? Promise.resolve() 
                        : Promise.reject(new Error('Password does not meet requirements'));
                    }
                  }
                ]}
              >
                <Input.Password
                  data-testid="reset-password-new-password-input"
                  prefix={<LockOutlined style={{ color: "rgba(0,0,0,.25)" }} />}
                  placeholder="Enter new password"
                  onChange={(e) => handlePasswordChange(e.target.value)}
                />
              </Form.Item>

              {/* Password Requirements */}
              {passwordValidation.requirements.length > 0 && (
                <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f6f6f6', borderRadius: '6px' }}>
                  <strong style={{ fontSize: '14px' }}>Password Requirements:</strong>
                  <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', fontSize: '13px' }}>
                    {passwordValidation.requirements.map((req) => (
                      <li key={req.key} style={{ 
                        color: req.valid ? '#52c41a' : '#ff4d4f',
                        marginBottom: '4px'
                      }}>
                        {req.valid ? '✓' : '✗'} {req.text}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Confirm Password field */}
              <Form.Item
                name="confirmPassword"
                label="Confirm New Password"
                dependencies={['password']}
                rules={[
                  {
                    required: true,
                    message: "Please confirm your new password!",
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('The two passwords do not match!'));
                    },
                  }),
                ]}
              >
                <Input.Password
                  data-testid="reset-password-confirm-password-input"
                  prefix={<LockOutlined style={{ color: "rgba(0,0,0,.25)" }} />}
                  placeholder="Confirm new password"
                />
              </Form.Item>

              {/* Submit button */}
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  className="signIn-form-button"
                  data-testid="reset-password-complete-submit-button"
                  loading={loading}
                  disabled={!passwordValidation.isValid}
                >
                  Reset Password
                </Button>
              </Form.Item>
            </Form>
            
            <div style={{ display: "flex", justifyContent: "center" }}>
              <Link to="/signin">Back to Log in</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordForm;